import {
  Sparkles, Wand2, Layout, Palette, Rocket, Target,
  CalendarCheck, Pencil, ShoppingBag, Smartphone,
} from "lucide-react";

export const HERO_CHIPS = [
  { icon: Layout, label: "AI-built website" },
  { icon: Rocket, label: "Hosting + Domain Included" },
  { icon: Target, label: "Audience + 30-day plan" },
];

export const WHAT_IT_DOES = [
  { icon: Wand2, title: "Builds your website with AI", text: "Describe your idea in one sentence. Interest to Income generates a fully-designed, mobile-ready site — copy and layout included." },
  { icon: Target, title: "Helps identify your target audience", text: "We surface who your ideal customer is, what they care about, and where to find them." },
  { icon: CalendarCheck, title: "Creates a 30-day action plan", text: "A clear, tactical weekly plan so you know exactly what to do — no marketing guesswork." },
  { icon: Pencil, title: "Gives you tools to edit and publish", text: "Visual editor + one-click publish. No code. Free plan available for 30 days." },
];

export const HOW_STEPS = [
  { icon: Wand2, title: "Describe your idea", text: "One sentence — we handle the rest." },
  { icon: Layout, title: "Website built for you", text: "AI-designed, mobile-friendly, industry-aware." },
  { icon: Target, title: "Audience data + plan", text: "Who to reach and how to grow." },
  { icon: Rocket, title: "Publish in minutes", text: "Custom domain + hosting included." },
];

export const WHAT_YOU_GET = [
  { icon: Palette, title: "AI website builder", text: "One prompt → full website." },
  { icon: Sparkles, title: "AI images", text: "Product & lifestyle photos generated for you." },
  { icon: Target, title: "Target audience data", text: "Know exactly who to reach." },
  { icon: CalendarCheck, title: "30-day action plan", text: "Simple weekly tasks to launch." },
  { icon: Pencil, title: "Easy website editor", text: "Click any block to edit — no code." },
  { icon: ShoppingBag, title: "Ecommerce ready", text: "Sell products or services with Stripe." },
  { icon: Smartphone, title: "Mobile-friendly", text: "Every site looks great on phones." },
];

/**
 * Iter 57 — Realistic mini-website previews for the "See what others are
 * building" section. Each entry drives a compact but true-to-life render of
 * the corresponding SRC template (ecommerce / restaurant / service).
 *
 * Shape:
 *   variant      : "ecommerce" | "restaurant" | "service"
 *   slug         : kebab-case brand slug (used for data-testids only)
 *   name         : Brand name shown in the mini-nav
 *   tag          : Category chip (e.g. "Real Estate", "Ecommerce")
 *   heroImage    : AI-generated hero photo URL served by /api/ai-images
 *   accent       : Primary brand accent hex
 *   accentCream? : Restaurant-only secondary cream color
 *   navLinks     : Array of 3–4 nav items (uppercased on ecommerce)
 *   heroEyebrow  : Eyebrow line above the headline
 *   heroHeadline?: Single-line headline (used by service + restaurant)
 *   heroHeadline1/2/3?: Three-part uppercase headline (ecommerce only)
 *   heroSub      : Subheadline paragraph
 *   ctaPrimary   : Primary CTA text
 *   ctaSecondary?: Secondary CTA text (ecommerce + service)
 *   subSection   : "Peek" of the section below the hero — service/restaurant
 *                  render a title + 3 mini feature/menu cards, ecommerce
 *                  renders a 3-up product grid.
 */
export const EXAMPLES = [
  {
    // Service SRC — Luxury real estate agency (Cornerstone Realty).
    variant: "service",
    slug: "cornerstone-realty",
    name: "Cornerstone Realty",
    tag: "Real Estate",
    heroImage: "/api/ai-images/landing_ex_realestate_v1.png",
    accent: "#c85a3c", // matches template-service orange-terracotta
    navLinks: ["Homes for Sale", "Services", "About", "Contact"],
    heroEyebrow: "LICENSED · LOCAL · TRUSTED",
    heroHeadline: "Homes you'll love, priced right.",
    heroSub: "A family-run realty team helping first-time buyers and long-time homeowners land the right home — with honest advice at every step.",
    ctaPrimary: "Book a Consultation",
    ctaSecondary: "View Listings",
    subSection: {
      eyebrow: "WHAT WE DO",
      title: "Every part of the move, handled.",
      cards: [
        { title: "Buyer Representation", body: "Find the right home in your budget." },
        { title: "Home Selling", body: "Price, market, and close in weeks." },
        { title: "Investment Advisory", body: "Rental & flip strategy." },
      ],
    },
  },
  {
    // Restaurant SRC — Italian trattoria (Bella Roma).
    variant: "restaurant",
    slug: "bella-roma",
    name: "Bella Roma Italian Kitchen",
    tag: "Restaurant",
    heroImage: "/api/ai-images/landing_ex_italian_v1.png",
    accent: "#2f4f2f",     // forest green
    accentCream: "#f7f1e3",
    navLinks: ["Menu", "Reservations", "About", "Contact"],
    heroEyebrow: "TRATTORIA · WINE BAR · SINCE 1998",
    heroHeadline: "A taste of Rome, made fresh nightly.",
    heroSub: "Hand-tossed pizza, homemade pasta, and a curated Italian wine list — served in a warm room built for lingering.",
    ctaPrimary: "Reserve a Table",
    ctaSecondary: "View The Menu",
    subSection: {
      eyebrow: "SIGNATURE DISHES",
      title: "Fresh from the kitchen.",
      cards: [
        { title: "Margherita", body: "San Marzano, mozzarella di bufala, basil." },
        { title: "Cacio e Pepe", body: "Tonnarelli, pecorino, black pepper." },
        { title: "Tiramisu", body: "Espresso-soaked ladyfingers, mascarpone." },
      ],
    },
  },
  {
    // Ecommerce SRC — Boutique hat store (Crown & Stitch Hats).
    variant: "ecommerce",
    slug: "crown-and-stitch-hats",
    name: "Crown & Stitch Hats",
    tag: "Ecommerce",
    heroImage: "/api/ai-images/landing_ex_hatstore_v1.png",
    accent: "#C2410C",
    navLinks: ["Shop All", "Fedoras", "Baseball Caps", "Panama"],
    heroEyebrow: "FALL COLLECTION — DROP 04",
    heroHeadline1: "HATS FOR",
    heroHeadline2: "every",
    heroHeadline3: "OCCASION.",
    heroSub: "Handmade fedoras, panamas, and structured baseball caps. Small batches. Built to be worn every day.",
    ctaPrimary: "Shop The Drop",
    ctaSecondary: "Explore Collection",
    subSection: {
      eyebrow: "BESTSELLERS",
      products: [
        { title: "The Ashwood Fedora", price: "$149" },
        { title: "The Coastal Panama", price: "$129" },
        { title: "Signature Ballcap", price: "$59" },
      ],
    },
  },
];
